/**
 * The life of a payment order, as an explicit graph.
 *
 * ## Why the transitions are data
 *
 * A payment provider notifies asynchronously, more than once, and out of order.
 * The dangerous case is not the happy path - it is a notification that arrives
 * ten minutes after an order expired, saying the payment went through. If
 * "which state may follow which" lived in scattered `if`s, that late message
 * would eventually walk an expired order back to `pending` and then to
 * `granted`, and somebody would receive Cash for an order the system had
 * already closed and possibly re-sold.
 *
 * So the graph is a table, `canTransitionOrder` is the only thing allowed to answer
 * the question, and a refused move is recorded as a divergence rather than
 * silently dropped.
 *
 * ## approved is not granted
 *
 * `approved` means the PROVIDER confirmed the money. `granted` means KENOCYBER
 * delivered the benefit. They are two states, not one, because the process can
 * die between them: a crash after approval must be resumable, and the way to
 * make it resumable is for "approved but not yet granted" to be a state the
 * system can find and finish.
 */

export const STORE_ORDER_STATES = [
  /** Row created. Nothing has been sent to the provider yet. */
  'created',
  /** The provider has the charge and is waiting for the payer. */
  'pending',
  /** The provider confirmed the money. Nothing has been delivered yet. */
  'approved',
  /** Kenocyber delivered the Cash or the VIP. The happy ending. */
  'granted',
  /** The provider refused it. */
  'rejected',
  /** Cancelled before payment, by the player or by us. */
  'cancelled',
  /** The charge's window closed unpaid. */
  'expired',
  /** A refund has been asked for and not yet confirmed. */
  'refund_pending',
  /** The refund is confirmed. The entitlement is reversed. */
  'refunded',
  /** Something did not add up. No automation touches it again. */
  'manual_review',
] as const;

export type StoreOrderState = (typeof STORE_ORDER_STATES)[number];

/**
 * Where each state may go, under AUTOMATION.
 *
 * Everything a webhook, a poll or a scheduled sweep is allowed to do is in this
 * table. Note what is not here: nothing returns to `created`, nothing leaves a
 * closed state except into `manual_review`, and `granted` can only be unwound
 * through a refund.
 */
export const STORE_ORDER_TRANSITIONS: Readonly<
  Record<StoreOrderState, readonly StoreOrderState[]>
> = Object.freeze({
  created: Object.freeze([
    'pending',
    'rejected',
    'cancelled',
    'expired',
    'manual_review',
  ] as StoreOrderState[]),
  pending: Object.freeze([
    'approved',
    'rejected',
    'cancelled',
    'expired',
    'manual_review',
  ] as StoreOrderState[]),
  // The only automated move out of `approved` is the delivery itself.
  approved: Object.freeze(['granted', 'refund_pending', 'manual_review'] as StoreOrderState[]),
  // Delivered. It can still be refunded, and nothing else.
  granted: Object.freeze(['refund_pending', 'manual_review'] as StoreOrderState[]),
  rejected: Object.freeze(['manual_review'] as StoreOrderState[]),
  cancelled: Object.freeze(['manual_review'] as StoreOrderState[]),
  expired: Object.freeze(['manual_review'] as StoreOrderState[]),
  refund_pending: Object.freeze(['refunded', 'manual_review'] as StoreOrderState[]),
  refunded: Object.freeze(['manual_review'] as StoreOrderState[]),
  /*
   * OPERATOR ONLY, and there is no route.
   *
   * A human resolving a divergence may push an order to any of these. Nothing
   * automated is allowed to: `manual_review` exists precisely to stop the
   * automation guessing. `AUTOMATED_TRANSITIONS` below is what the service
   * actually consults.
   */
  manual_review: Object.freeze([
    'approved',
    'granted',
    'rejected',
    'cancelled',
    'refund_pending',
    'refunded',
  ] as StoreOrderState[]),
});

/**
 * States automation will never move on from, except into review.
 *
 * A provider notification about one of these is information, not an
 * instruction.
 */
export const STORE_ORDER_CLOSED_STATES: readonly StoreOrderState[] = Object.freeze([
  'rejected',
  'cancelled',
  'expired',
  'refunded',
]);

/** True once nothing is expected to happen on its own. */
export function isClosedOrderState(state: StoreOrderState): boolean {
  return STORE_ORDER_CLOSED_STATES.includes(state);
}

/**
 * The same graph with every operator-only edge removed.
 *
 * The service consults THIS. Keeping the full graph as well means the
 * difference between "a human may" and "a webhook may" is written down instead
 * of being a comment somebody has to trust.
 */
export const AUTOMATED_TRANSITIONS: Readonly<Record<StoreOrderState, readonly StoreOrderState[]>> =
  Object.freeze({
    ...STORE_ORDER_TRANSITIONS,
    manual_review: Object.freeze([] as StoreOrderState[]),
  });

/**
 * May automation move an order from `from` to `to`?
 *
 * A move to the same state is allowed and means nothing happened - which is the
 * ordinary case for a repeated notification and must not be an error.
 */
export function canTransitionOrder(from: StoreOrderState, to: StoreOrderState): boolean {
  return from === to || (AUTOMATED_TRANSITIONS[from]?.includes(to) ?? false);
}

/** May an operator, resolving a review by hand, move it? */
export function canTransitionOrderManually(from: StoreOrderState, to: StoreOrderState): boolean {
  return from === to || (STORE_ORDER_TRANSITIONS[from]?.includes(to) ?? false);
}

/**
 * What the provider says, normalised away from any one provider's vocabulary.
 *
 * Mercado Pago says `approved`/`rejected`/`in_process`/`cancelled`/`refunded`;
 * another provider will say something else. Adapters map onto THIS, and the
 * store's state machine only ever sees these five - so adding a provider is a
 * mapping, not a change to the graph above.
 */
export const PROVIDER_PAYMENT_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'cancelled',
  'refunded',
  /** The provider answered something this adapter does not recognise. */
  'unknown',
] as const;
export type ProviderPaymentStatus = (typeof PROVIDER_PAYMENT_STATUSES)[number];

/**
 * The order state a provider status implies, before any check has been made.
 *
 * DELIBERATELY not the whole decision. `approved` here means "the provider says
 * it is paid", and the service still has to agree the amount, the currency, the
 * external reference and the recipient before it writes that state. See
 * `docs/adr/0015-store-orders-and-payment-provider.md`.
 */
export function orderStateForProviderStatus(status: ProviderPaymentStatus): StoreOrderState | null {
  switch (status) {
    case 'pending':
      return 'pending';
    case 'approved':
      return 'approved';
    case 'rejected':
      return 'rejected';
    case 'cancelled':
      return 'cancelled';
    case 'refunded':
      return 'refunded';
    default:
      // An unrecognised status is a reason to look, not a reason to guess.
      return null;
  }
}
